import React, { useEffect, useState } from 'react';
import MetricCard from './MetricCard';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import { useDataProvider, useNotify } from 'react-admin';
import { formatEnCaRange } from '../../../utils';
import { getRankingInfoTooltipDescription, getRankingInfoTooltipTitle } from '../utils';
import Spinner from '../../../Components/Spinner';

const useStylesCustom = makeStyles(theme => ({
    cardsWrapper: {
        display: 'flex',
        justifyContent: 'space-between',
        height: 260,
        flexWrap: 'wrap',
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(8),
        [theme.breakpoints.down('md')]: {
            height: 'auto',
            justifyContent: 'space-around',
            marginBottom: theme.spacing(2),
        },
    },
    contentWrapper: {
        position: 'relative',
        maxWidth: '1100px',
        alignSelf: 'center',
        width: '100%',
        [theme.breakpoints.up('md')]: {
            padding: `0 0 0 ${theme.spacing(2)}px`,
        },
    },
    container: {
        justifyContent: 'center',
    },
}))

const MetricCardsSection = ({ range, serviceType }) => {
    const classes = useStylesCustom()
    const dataProvider = useDataProvider()
    const notify = useNotify()

    const [prospectConversionData, setProspectConversionData] = useState(null)
    const [recommendationsPerClientData, setRecommendationsPerClientData] = useState(null)
    const [potentialRiskData, setPotentialRiskData] = useState(null)
    const [prospectStatsFetched, setProspectStatsFetched] = useState(false)
    const [recommendationsStatsFetched, setRecommendationsStatsFetched] = useState(false)
    const [riskStatsFetched, setRiskStatsFetched] = useState(false)

    const fetchProspectConversion = () => {
        const queryStringParams = formatEnCaRange(range)
        queryStringParams.service_type_id = serviceType

        dataProvider('GET_ONE', 'elevation/scores/prospect-conversion', { queryStringParams })
            .then(res => {
                setProspectConversionData(res.data)
                setProspectStatsFetched(true)
            })
            .catch((e) => {
                notify(e.message, 'warning')
            })
    }

    const fetchRecommendationsPerClient = () => {
        const queryStringParams = formatEnCaRange(range)
        queryStringParams.service_type_id = serviceType

        dataProvider('GET_ONE', 'elevation/scores/recommendations-per-client', { queryStringParams })
            .then(res => {
                setRecommendationsPerClientData(res.data)
                setRecommendationsStatsFetched(true)
            })
            .catch((e) => {
            notify(e.message, 'warning')
        })
    }

    const fetchPotentialRisk = () => {
        const queryStringParams = formatEnCaRange(range)
        queryStringParams.service_type_id = serviceType

        dataProvider('GET_ONE', 'elevation/scores/consumer-duty', { queryStringParams })
            .then(res => {
                setPotentialRiskData(res.data)
                setRiskStatsFetched(true)
            })
            .catch((e) => {
                notify(e.message, 'warning')
            })
    }

    useEffect(()=> {
        setProspectStatsFetched(false)
        setRecommendationsStatsFetched(false)
        setRiskStatsFetched(false)

        fetchRecommendationsPerClient()
        fetchPotentialRisk()
        fetchProspectConversion()
    },[range, serviceType])

    if (!prospectStatsFetched || !recommendationsStatsFetched || !riskStatsFetched) {
        return <Spinner />
    }

    return (
        <Grid container className={classes.container}>
            <div className={classes.contentWrapper}>
                <div className={classes.cardsWrapper}>
                    {recommendationsPerClientData &&
                        <MetricCard
                            title='Recommendations per client'
                            main={true}
                            mainNonClickable={true}
                            active={true}
                            value={recommendationsPerClientData.recommendations_per_client?.toFixed(2) ?? '-'}
                            tooltipTitle={getRankingInfoTooltipTitle(recommendationsPerClientData.recommendations_per_client_rank_percent)}
                            tooltipDescription={getRankingInfoTooltipDescription(recommendationsPerClientData.recommendations_per_client_rank_percent)}
                        />
                    }
                    {potentialRiskData &&
                        <MetricCard
                            title='Potential risk'
                            main={true}
                            mainNonClickable={true}
                            active={true}
                            value={potentialRiskData.consumer_duty_risk_markers_percent !== null ? `${Math.round(potentialRiskData.consumer_duty_risk_markers_percent)}%` : '-'}
                            tooltipTitle={getRankingInfoTooltipTitle(potentialRiskData.consumer_duty_score_rank_percent)}
                            tooltipDescription={getRankingInfoTooltipDescription(potentialRiskData.consumer_duty_score_rank_percent)}
                        />
                    }
                    {prospectConversionData &&
                        <MetricCard
                            title='Prospect conversion'
                            main={true}
                            mainNonClickable={true}
                            active={true}
                            value={prospectConversionData.prospect_conversion_score !== null ? `${Math.round(prospectConversionData.prospect_conversion_score)}%` : '-'}
                            tooltipTitle={getRankingInfoTooltipTitle(prospectConversionData.prospect_conversion_rank_percent)}
                            tooltipDescription={getRankingInfoTooltipDescription(prospectConversionData.prospect_conversion_rank_percent)}
                        />
                    }
                </div>
            </div>
        </Grid>
    )
}

export default MetricCardsSection